import React from 'react';
import { CATEGORIES, catColor, categorizeEvent } from '../../config/categories';
import { formatEventHostsLabel, isEventCanceled } from '../../utils/eventUtils';
import { fs } from '../../utils/typography';

// Month-cell event chip — "bar" treatment: no background of its own, just a
// 3px rounded category-color bar + truncated title + uppercase mono org line.
// `compact` (landscape phones, where month cells are ~40px tall) drops the org
// line and shrinks the title so two chips fit per cell. `interactive={false}`
// renders it as display-only, so a tap lands on the surrounding day cell —
// landscape cells are too small to reliably hit a chip vs. the day.
const EventChip = ({ event, tokens, onClick, compact = false, interactive = true }) => {
    const c = tokens.colors;
    const cat = categorizeEvent(event);
    const cc = catColor(cat, tokens);
    const canceled = isEventCanceled(event);
    const hostsLabel = formatEventHostsLabel(event);
    return (
        <div
            style={{ display: 'flex', gap: compact ? 5 : 7, alignItems: 'stretch', padding: compact ? '1px 0' : '2px 0', cursor: 'pointer', opacity: canceled ? 0.55 : 1, minWidth: 0 }}
            {...(interactive ? {
                onClick,
                role: 'button',
                tabIndex: 0,
                onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClick(e);
                    }
                },
                'aria-label': `View details for ${event.title} by ${hostsLabel}${canceled ? ' (canceled)' : ''}`,
            } : { 'aria-hidden': true })}
            title={`${event.title} — ${hostsLabel}${canceled ? ' (Canceled)' : ''}`}
        >
            <div style={{ width: 3, borderRadius: 2, background: cc.strong, flex: '0 0 auto' }} />
            <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                    fontFamily: tokens.fonts.ui, fontSize: compact ? fs(11.5) : fs(14), fontWeight: 600, color: c.ink, lineHeight: compact ? 1.2 : 1.25,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    textDecoration: canceled ? 'line-through' : 'none',
                }}>
                    <i className={`fas ${CATEGORIES[cat].icon}`} style={{ color: cc.strong, fontSize: fs(9.5), marginRight: 3 }} aria-hidden="true" />
                    {canceled && (
                        <i className="fas fa-ban" style={{ color: c.faint, fontSize: fs(11), marginRight: 4 }} aria-label="Canceled event" />
                    )}
                    {!canceled && event.eventType === 'Featured' && (
                        <i className="fas fa-star" style={{ color: cc.strong, fontSize: fs(11), marginRight: 4 }} aria-label="Featured event" />
                    )}
                    {event.title}
                </div>
                {!compact && (
                    <div style={{
                        fontFamily: tokens.fonts.mono, fontSize: fs(11), fontWeight: 500, letterSpacing: 0.2, color: c.faint,
                        textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1,
                    }}>{hostsLabel}</div>
                )}
            </div>
        </div>
    );
};

export default EventChip;
